import { Plus } from "lucide-react";

/**
 * ConnectorsPanel — ERP / WMS / custom-API + webhook integrations
 * (Package C). Lists connected 3rd parties, event log, plus an
 * "Install" bar for major 3PLs. Editor dialog stays in the parent.
 *
 * Extracted from SellerDropship.jsx in Phase 2 refactor.
 */
export default function ConnectorsPanel({
  connectors, connectorMeta, connectorEvents,
  setConnectorDraft, testConnector, deleteConnector,
  showConnectorEvents, emptyConnectorDraft,
  syncConnector, emitTestEvent,
}) {
  return (
  <div className="rounded-2xl border border-indigo-200 bg-white overflow-hidden" data-testid="fulfillment-connectors-panel">
    <div className="px-5 py-3 border-b border-indigo-200 bg-indigo-50/50 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-[13px] font-medium">Connectors</div>
        <div className="text-[11px] text-foreground/55 mt-0.5">
          Wire an external ERP, WMS, ShipStation, or a custom webhook. Events fire out when orders ship or tracking updates.
        </div>
      </div>
      <div className="flex items-center gap-2">
        {connectorMeta.kinds.map(k => (
          <button key={k}
            onClick={() => setConnectorDraft(emptyConnectorDraft(k))}
            data-testid={`connector-add-${k}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-[11px] mono uppercase tracking-[0.14em] border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-100">
            <Plus className="w-3 h-3"/>{k.replace("_", " ")}
          </button>
        ))}
      </div>
    </div>
    {connectors.length === 0 ? (
      <div className="p-10 text-center text-[12px] text-foreground/55">
        No connectors yet. Click one of the buttons above to wire ShipStation, QuickBooks, NetSuite, or a custom endpoint.
      </div>
    ) : (
      <div className="divide-y divide-border">
        {connectors.map(c => (
          <div key={c.id} data-testid={`connector-${c.id}`} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-2 h-2 rounded-full ${c.is_active ? "bg-emerald-500" : "bg-neutral-300"}`}/>
              <div className="min-w-0">
                <div className="text-[13px] font-medium">
                  {c.name}
                  <span className="ml-2 inline-flex text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                    {c.kind}
                  </span>
                  <span className="ml-1 inline-flex text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-secondary text-foreground/70">
                    {c.direction}
                  </span>
                </div>
                <div className="text-[10.5px] text-foreground/55 mt-0.5">
                  pushed {c.stats?.pushed || 0} · imported {c.stats?.imported || 0} · failed {c.stats?.failed || 0}
                  {c.last_error && <span className="text-rose-700"> · last error: {c.last_error.slice(0, 60)}</span>}
                  {(c.events || []).length > 0 && <span> · {c.events.length} event{c.events.length === 1 ? "" : "s"}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={() => testConnector(c.id)}
                data-testid={`connector-test-${c.id}`}
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">Test</button>
              {["import", "both"].includes(c.direction) && (
                <button onClick={() => syncConnector(c.id)}
                  data-testid={`connector-sync-${c.id}`}
                  className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">Sync</button>
              )}
              {["push", "both"].includes(c.direction) && (
                <button onClick={() => emitTestEvent(c.id, (c.events || [])[0] || "order.created")}
                  data-testid={`connector-emit-${c.id}`}
                  className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">Emit</button>
              )}
              <button onClick={() => showConnectorEvents(c.id)}
                data-testid={`connector-events-${c.id}`}
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">Events</button>
              <button onClick={() => setConnectorDraft({ ...c })}
                data-testid={`connector-edit-${c.id}`}
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary">Edit</button>
              <button onClick={() => deleteConnector(c.id)}
                data-testid={`connector-delete-${c.id}`}
                className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200">Delete</button>
            </div>
          </div>
        ))}
      </div>
    )}
    {connectorEvents.list.length > 0 && (
      <div className="border-t border-border bg-secondary/20 p-4" data-testid="connector-events-log">
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/60 mb-2">
          Last {connectorEvents.list.length} events · connector {connectorEvents.cid?.slice(0, 8)}…
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {connectorEvents.list.map((e, i) => (
            <div key={i} className="text-[11.5px] mono flex items-center gap-3">
              <span className={e.ok ? "text-emerald-700" : "text-rose-700"}>{e.ok ? "OK " : "ERR"}</span>
              <span className="text-foreground/60 text-[10.5px]">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
              <span className="text-indigo-700">{e.event}</span>
              <span className="text-foreground/70 truncate">{e.detail}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
  );
}
